/** EjecutivaTab — Resumen ejecutivo mensual (Avance Tickets SAC).
 *
 * Filtro: Año. Tabla con filas = Canal de entrada, columnas agrupadas en
 * Recibidos / Atendidos por mes (meses transcurridos), fila Total y fila SLA
 * (días calendario). Resalta el mes actual.
 */
import React, { useCallback, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Loader2, RefreshCw, TrendingUp } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useAbortableLoad, isCanceled } from '../../../hooks/useAbortableLoad';

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 2023 }, (_, i) => currentYear - i);

export default function EjecutivaTab() {
  const { api, empresa } = useAuth();
  const [anio, setAnio] = useState(String(currentYear));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (signal) => {
    if (!empresa?.modulo_tickets_activo) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await api.get('/tickets/bi/ejecutiva', { params: { anio }, signal });
      setData(r.data);
    } catch (e) {
      if (isCanceled(e)) return;
      console.error('Error ejecutiva:', e);
      setData(null);
    } finally { setLoading(false); }
  }, [api, empresa, anio]);

  useAbortableLoad((signal) => fetchData(signal), [anio]);

  const meses = data?.meses || [];
  const canales = data?.canales || [];
  const totales = data?.totales || { recibidos: {}, atendidos: {} };
  const sla = data?.sla_dias_calendario || {};
  const mesActual = data?.mes_actual;
  const solic = data?.solicitudes || { filas: [], totales_mes: {}, gran_total: 0 };
  const cierres = data?.cierres_mes || { mes_nombre: '', total: 0, bandas: [] };

  const isCur = (m) => m === mesActual;
  const curCol = 'bg-[#4A7F80]/10';

  return (
    <div className="space-y-8">
      {/* Filtro */}
      <div className="rounded-xl border border-[#d9d2c4] bg-[#f3f0e9] p-4 flex items-end gap-3" data-testid="ejec-filtros">
        <div className="space-y-1">
          <Label className="text-xs text-slate-600">Año</Label>
          <Select value={anio} onValueChange={setAnio}>
            <SelectTrigger data-testid="ejec-anio" className="bg-white w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="ghost" onClick={() => fetchData()} data-testid="ejec-refrescar" className="text-slate-600 ml-auto">
          <RefreshCw className="w-4 h-4 mr-1" /> Refrescar
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
      ) : !data ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="ejec-error">
          No se pudieron cargar los datos. Asegúrate de que la vista <code>vista_tickets_bi</code> exista en Supabase.
        </div>
      ) : meses.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500" data-testid="ejec-vacio">
          No hay meses transcurridos para el año seleccionado.
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {/* Tabla principal Avance Tickets SAC */}
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#4A7F80]" />
                <span className="text-sm font-semibold text-slate-700">Avance Tickets SAC · {anio}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse" data-testid="ejec-tabla">
                  <thead>
                    <tr>
                      <th rowSpan={2} className="sticky left-0 z-10 bg-[#6AA1A0] text-white text-left px-3 py-2 align-bottom">Canal (Unidad)</th>
                      <th colSpan={meses.length} className="px-2 py-2 text-center bg-[#4C5562] text-white font-semibold">Recibidos</th>
                      <th colSpan={meses.length} className="px-2 py-2 text-center bg-[#4C5562] text-white font-semibold border-l-2 border-white">Atendidos</th>
                    </tr>
                    <tr>
                      {meses.map((m) => (
                        <th key={`r-${m.num}`} className={`px-2 py-1.5 text-center font-medium text-white bg-[#96945C] ${isCur(m.num) ? curCol : ''}`}>{m.nombre}</th>
                      ))}
                      {meses.map((m, i) => (
                        <th key={`a-${m.num}`} className={`px-2 py-1.5 text-center font-medium text-white bg-[#96945C] ${i === 0 ? 'border-l-2 border-white' : ''} ${isCur(m.num) ? curCol : ''}`}>{m.nombre}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {canales.length === 0 ? (
                      <tr><td colSpan={meses.length * 2 + 1} className="text-center text-slate-400 italic py-8">Sin datos en el año seleccionado.</td></tr>
                    ) : canales.map((c, idx) => (
                      <tr key={c.canal} className={`border-t border-slate-100 hover:bg-slate-50 ${idx % 2 ? 'bg-[#F5F5F5]' : 'bg-white'}`} data-testid={`ejec-fila-${c.canal}`}>
                        <td className={`sticky left-0 z-10 px-3 py-2 font-medium text-slate-700 whitespace-nowrap ${idx % 2 ? 'bg-[#F5F5F5]' : 'bg-white'}`}>{c.canal}</td>
                        {meses.map((m) => (
                          <td key={`r-${m.num}`} className={`px-2 py-2 text-center tabular-nums ${isCur(m.num) ? curCol : ''}`}>{c.recibidos[m.num] || ''}</td>
                        ))}
                        {meses.map((m, i) => (
                          <td key={`a-${m.num}`} className={`px-2 py-2 text-center tabular-nums ${i === 0 ? 'border-l-2 border-slate-200' : ''} ${isCur(m.num) ? curCol : ''}`}>{c.atendidos[m.num] || ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-black text-white font-semibold" data-testid="ejec-total">
                      <td className="sticky left-0 z-10 bg-black px-3 py-2">Total</td>
                      {meses.map((m) => (
                        <td key={`tr-${m.num}`} className="px-2 py-2 text-center tabular-nums">{totales.recibidos[m.num] || 0}</td>
                      ))}
                      {meses.map((m, i) => (
                        <td key={`ta-${m.num}`} className={`px-2 py-2 text-center tabular-nums ${i === 0 ? 'border-l-2 border-white/30' : ''}`}>{totales.atendidos[m.num] || 0}</td>
                      ))}
                    </tr>
                    <tr className="bg-black text-white" data-testid="ejec-sla">
                      <td className="sticky left-0 z-10 bg-black px-3 py-2">SLA (días calendario)</td>
                      <td colSpan={meses.length} className="px-2 py-2 text-center text-white/40 text-xs">—</td>
                      {meses.map((m, i) => (
                        <td key={`sla-${m.num}`} className={`px-2 py-2 text-center tabular-nums ${i === 0 ? 'border-l-2 border-white/30' : ''}`}>
                          {sla[m.num] != null ? `${sla[m.num]}` : '—'}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="px-4 py-2 text-[11px] text-slate-400 border-t border-slate-100">
                Recibidos = tickets creados en el mes · Atendidos = tickets terminados en el mes · SLA = promedio de días calendario de cierre.
              </div>
            </CardContent>
          </Card>

          {/* Secciones adicionales: Solicitudes + Cierres */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ejec-extra">
            {/* Principales Solicitudes del cliente */}
            <Card>
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b border-slate-100">
                  <span className="text-sm font-semibold text-slate-700">Principales Solicitudes del cliente</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse" data-testid="ejec-solicitudes">
                    <thead>
                      <tr>
                        <th className="text-left px-3 py-2 bg-[#6AA1A0] text-white font-semibold">Categoría</th>
                        {meses.map((m) => (
                          <th key={`sm-${m.num}`} className="px-2 py-2 text-center bg-[#96945C] text-white font-medium">{m.nombre}</th>
                        ))}
                        <th className="px-2 py-2 text-center bg-[#96945C] text-white font-semibold">Total</th>
                        <th className="px-2 py-2 text-center bg-[#96945C] text-white font-semibold">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {solic.filas.length === 0 ? (
                        <tr><td colSpan={meses.length + 3} className="text-center text-slate-400 italic py-8">Sin solicitudes en el año.</td></tr>
                      ) : solic.filas.map((f, idx) => (
                        <tr key={f.clasificacion} className={idx % 2 ? 'bg-[#F5F5F5]' : 'bg-white'} data-testid={`ejec-solic-${idx}`}>
                          <td className="px-3 py-1.5 text-slate-700 whitespace-nowrap">{f.clasificacion}</td>
                          {meses.map((m) => (
                            <td key={`c-${m.num}`} className="px-2 py-1.5 text-center tabular-nums text-slate-700">{f.por_mes[m.num] || ''}</td>
                          ))}
                          <td className="px-2 py-1.5 text-center tabular-nums font-semibold text-slate-800">{f.total}</td>
                          <td className="px-2 py-1.5 text-center tabular-nums text-slate-600">{f.pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                    {solic.filas.length > 0 && (
                      <tfoot>
                        <tr className="bg-[#6AA1A0] text-white font-semibold" data-testid="ejec-solic-total">
                          <td className="px-3 py-2">Total general</td>
                          {meses.map((m) => (
                            <td key={`smt-${m.num}`} className="px-2 py-2 text-center tabular-nums">{solic.totales_mes[m.num] || 0}</td>
                          ))}
                          <td className="px-2 py-2 text-center tabular-nums">{solic.gran_total}</td>
                          <td className="px-2 py-2 text-center tabular-nums">100%</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Cierres del mes (días hábiles) */}
            <Card>
              <CardContent className="p-0">
                <div className="px-4 py-3 border-b border-slate-100">
                  <span className="text-sm font-semibold text-slate-700" data-testid="ejec-cierres-titulo">Cierres de {cierres.mes_nombre} (Días hábiles)</span>
                </div>
                <div className="p-5 bg-[#E8E8E8]" data-testid="ejec-cierres">
                  {cierres.total === 0 ? (
                    <p className="text-sm text-slate-500 italic py-6 text-center">Sin cierres en {cierres.mes_nombre || 'el mes'}.</p>
                  ) : (
                    <div className="space-y-5">
                      {cierres.bandas.map((b) => (
                        <div key={b.label} className="flex items-center gap-3" data-testid={`ejec-cierre-${b.label}`}>
                          <span className="text-xs text-slate-700 w-20 shrink-0 text-right">{b.label}</span>
                          <div className="flex-1 h-2.5 bg-white rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#6AA1A0] rounded-full transition-all"
                              style={{ width: `${b.pct}%` }}
                            />
                          </div>
                          <span className="text-xs font-semibold text-slate-700 w-16 shrink-0 tabular-nums text-right">{b.pct}% ({b.count})</span>
                        </div>
                      ))}
                      <div className="text-[11px] text-slate-500 pt-1">Total cerrados en {cierres.mes_nombre}: {cierres.total}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
